import React, { useState } from 'react';
import { User, Sparkles, Wrench, ChevronRight, ChevronDown } from 'lucide-react';
import type {
  WorkbenchMessage, MessageContentBlock, ToolUseBlock, ToolResultBlock,
} from '../../types';
import { dataContentProfilerApi } from '../../services/api';
import { renderMarkdown } from '../agents/shared/markdown';
import { SchemaProposalTable } from '../source-profiling/agent/blocks/components/tables/SchemaProposalTable';
import type { AgentSpec, AgentMessageBubbleProps } from '../agents/shared/types';

/**
 * Data Content Profiler bubble: short assistant narration (markdown) +
 * compact tool indicators, AND — after propose_schema_labels — the
 * COMBINED per-column table (schema + profile stats) rendered from
 * artifacts.preview.columns. Raw tool_results for the detail tools are
 * suppressed (the table replaces them). Chat plumbing is shared.
 */

const ToolUseChip: React.FC<{ block: ToolUseBlock }> = ({ block }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-block text-left bg-cargo-surface border border-cargo-border/60 rounded-lg px-3 py-2 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-cargo-muted hover:text-cargo-text"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Wrench className="w-3 h-3 text-cargo-accent" />
        <span className="font-mono text-cargo-accent">{block.name}</span>
        <span className="text-cargo-muted">called</span>
      </button>
      {open && (
        <pre className="mt-2 bg-cargo-panel/60 border border-cargo-border/40 rounded p-2 text-[10px] text-cargo-muted overflow-auto max-h-40">
          {JSON.stringify(block.input ?? {}, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ToolResultChip: React.FC<{ block: ToolResultBlock }> = ({ block }) => {
  const [open, setOpen] = useState(false);
  const text = typeof block.content === 'string'
    ? block.content
    : JSON.stringify(block.content, null, 2);
  return (
    <div className="inline-block text-left bg-cargo-surface border border-cargo-border/60 rounded-lg px-3 py-2 text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-cargo-muted hover:text-cargo-text"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span>tool result</span>
      </button>
      {open && (
        <pre className="mt-2 bg-cargo-panel/60 border border-cargo-border/40 rounded p-2 text-[10px] text-cargo-muted overflow-auto max-h-60 whitespace-pre-wrap">
          {text}
        </pre>
      )}
    </div>
  );
};

const Block: React.FC<{ block: MessageContentBlock; isUser: boolean }> = ({ block, isUser }) => {
  if (block.type === 'text') {
    return (
      <div
        className={`inline-block text-left px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-cargo-accent text-white'
            : 'bg-cargo-panel border border-cargo-border text-cargo-text'
        }`}
      >
        {renderMarkdown(block.text || '', isUser)}
      </div>
    );
  }
  if (block.type === 'tool_use') return <ToolUseChip block={block as ToolUseBlock} />;
  if (block.type === 'tool_result') return <ToolResultChip block={block as ToolResultBlock} />;
  return null;
};

/** The combined table + observations rendered right after the
 *  propose_schema_labels tool call (from artifacts.preview). */
const SchemaDetail: React.FC<{ artifacts: Record<string, any> }> = ({ artifacts }) => {
  const preview = artifacts?.preview;
  const columns = Array.isArray(preview?.columns) ? preview.columns : [];
  const observations = Array.isArray(preview?.observations) ? preview.observations : [];
  if (columns.length === 0) return null;
  return (
    <div className="mt-2 space-y-3">
      {/* Same SchemaProposalTable as the Profiling Agent — `profileStats`
          adds the Null % / Unique % / Samples columns from `_raw`.
          One component, no duplicate table. */}
      <SchemaProposalTable columns={columns} profileStats />
      {observations.length > 0 && (
        <div className="rounded-lg border border-cargo-border bg-cargo-panel/40 px-4 py-3">
          <div className="text-cargo-text text-[13px] font-semibold mb-1.5">
            Data Quality Observations
          </div>
          <ul className="list-disc ml-5 space-y-1 text-[13px] text-cargo-muted">
            {observations.map((o: any, i: number) => (
              <li key={i}>
                {o?.category ? <span className="text-cargo-text">{o.category}: </span> : null}
                {o?.description}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const DataContentMessageBubble: React.FC<AgentMessageBubbleProps> = ({
  message, artifacts, detailCardToolUseIds,
}) => {
  const isUser = message.role === 'user';
  const content = message.content;

  // Tool-result-only user messages → compact strip; hide the ones whose
  // matching tool_use already has a rich card (the combined table).
  if (isUser && Array.isArray(content) && content.every((b: any) => b?.type === 'tool_result')) {
    const visible = (content as ToolResultBlock[]).filter(
      (b) => !detailCardToolUseIds?.has(b.tool_use_id),
    );
    if (visible.length === 0) return null;
    // Align the chips under the content column with a w-8 avatar-gutter
    // spacer — same alignment the Profiling Agent's ToolResultsStrip
    // uses, so the chips line up neatly instead of sitting flush-left.
    return (
      <div className="flex gap-3 justify-start">
        <div className="w-8 flex-shrink-0" />
        <div className="flex gap-1 flex-wrap">
          {visible.map((b, i) => <ToolResultChip key={i} block={b} />)}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-cargo-accent/20' : 'bg-cargo-accent text-white'
        }`}
      >
        {isUser ? <User className="w-4 h-4 text-cargo-accent" /> : <Sparkles className="w-4 h-4" />}
      </div>
      <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className="text-cargo-muted text-[11px] uppercase tracking-wider mb-1">
          {isUser ? 'You' : 'Data Content Profiler'}
          {message.at && (
            <span className="ml-2 normal-case tracking-normal opacity-70">
              {new Date(message.at).toLocaleTimeString()}
            </span>
          )}
        </div>
        {typeof content === 'string' ? (
          <div
            className={`inline-block text-left px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
              isUser
                ? 'bg-cargo-accent text-white'
                : 'bg-cargo-panel border border-cargo-border text-cargo-text'
            }`}
          >
            {content}
          </div>
        ) : (
          <div className="space-y-2">
            {(content as MessageContentBlock[]).map((block, i) => (
              <div key={i}>
                <Block block={block} isUser={isUser} />
                {/* The combined Data Content table renders right after
                    the propose_schema_labels tool call. */}
                {block.type === 'tool_use'
                  && (block as ToolUseBlock).name === 'propose_schema_labels'
                  && <SchemaDetail artifacts={artifacts} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const dataContentProfilerSpec: AgentSpec = {
  title: 'Data Content Profiler',
  subtitle:
    'Profiles one table and explains what each column actually contains, plus its data-quality observations.',
  heroHeading: 'Profile a table’s content in chat',
  heroDescription:
    'Connect your database and pick a table — the agent explains, in plain '
    + 'language, what each column actually contains plus its data-quality '
    + 'observations. One table at a time, exact whole-table profiling.',
  placeholder: 'Give your database connection details and the table to profile…',

  backRoute: '/data-content-profiler',
  sessionRouteBase: '/data-content-profiler/sessions',
  queryKeyPrefix: 'data-content-profiler',
  streamUrlBase: '/api/data-content-profiler',

  api: dataContentProfilerApi,

  MessageBubble: DataContentMessageBubble,
  // Suppress the raw tool_result panels for these — the combined
  // Data Content table (rendered after propose_schema_labels) replaces them.
  detailCardTools: ['read_table_for_profiling', 'propose_schema_labels'],

  // #1 = the one-click DEMO (test credentials — no typing, just works).
  // #2-4 = concrete worked examples for real users (own credentials).
  samplePrompts: [
    {
      label: '▶ DEMO — profile DEMO_TGT.DIM_POLICY (test credentials)',
      prompt: 'Profile DEMO_TGT.DIM_POLICY using test credentials.',
      help: 'Format is SCHEMA.TABLE: DEMO_TGT is the schema, DIM_POLICY is the table — replace with your own. "using test credentials" = connect with the pre-configured demo Snowflake connection (no connection details entered). It then profiles every column of that one table.',
    },
    {
      label: 'Connect (Snowflake) + profile a table — example',
      help: 'Full connect-then-profile format. Replace each value with yours — account (e.g. ab12345.us-east-1), warehouse, database, schema, role, user, password. SALES.CUSTOMERS is SCHEMA.TABLE (SALES = schema, CUSTOMERS = table). It connects, then profiles that table.',
      prompt:
        'Connect to Snowflake — account: ab12345.us-east-1, warehouse: '
        + 'COMPUTE_WH, database: ANALYTICS, schema: SALES, role: ANALYST, '
        + 'user: jdoe, password: ******** — then profile SALES.CUSTOMERS '
        + 'and explain what each column contains.',
    },
    {
      label: 'Profile a specific table (already connected) — example',
      help: 'Use once already connected. SALES.CUSTOMERS = SCHEMA.TABLE (SALES = schema, CUSTOMERS = table — use yours). Profiles that one table and explains, per column, what it contains plus its data-quality issues.',
      prompt:
        'Profile SALES.CUSTOMERS — explain what each column contains '
        + 'plus its data-quality issues.',
    },
  ],
};
